// Copyright 2022, Imprivata, Inc.  All rights reserved.

import { type ApiErrorPayloadCommon, isApiError } from '@imprivata-cloud/common';
import type { CodingContext } from '@imprivata-cloud/data-privacy-js';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { usePlatform } from '../../context/platform/hooks';
import { apiClient } from '../client';

interface CreateEnrollmentResponse {
  id: string;
}

const createEnrollmentRequest = async (
  inquiryId: string,
  codingContext: CodingContext | undefined,
): Promise<string> => {
  const response = await apiClient.post<CreateEnrollmentResponse>('enroll', () => ({ inquiryId }), codingContext);
  return response.id;
};
export const useCreateEnrollmentMutation = (options: { onSuccess: (patientId: string) => void }) => {
  const { codingContext } = usePlatform();
  const [apiError, setApiError] = useState<ApiErrorPayloadCommon | undefined>(undefined);

  const mutation = useMutation({
    mutationFn: (inquiryId: string) => createEnrollmentRequest(inquiryId, codingContext),
    onError(error) {
      if (isApiError(error)) {
        setApiError(error);
      } else {
        setApiError({ error: { code: 'unknown', message: 'Unknown error' } });
      }
    },
    onSuccess: (patientId) => options.onSuccess(patientId),
  });

  return {
    createEnrollment: mutation.mutate,
    apiError,
    isPending: mutation.isIdle || mutation.isPending,
  };
};
